import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Trash2, Bell, BellOff, Send, CalendarClock, Plus } from "lucide-react";
import { hasPlan } from "@/lib/plans";
import UpgradeLock from "@/components/UpgradeLock";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const STAGES = [
  { id: "discovery", label: "Discovery" },
  { id: "triage", label: "Triage" },
  { id: "preparation", label: "Preparation" },
  { id: "submitted", label: "Submitted" },
  { id: "won", label: "Won" },
  { id: "lost", label: "Lost" },
];
const fmt = (n) => "$" + Number(n || 0).toLocaleString();
const inputCls = "rounded-md border border-slate-200 px-3.5 py-2.5 text-sm w-full bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500";
const emptyBid = { title: "", agency: "", amount: "", deadline: "", stage: "discovery", notes: "" };

export default function Crm() {
  const { user, refresh } = useAuth();
  const locked = !hasPlan(user?.plan, "pro");
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragId, setDragId] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [remindersOn, setRemindersOn] = useState(user?.reminders_enabled !== false);
  const [testing, setTesting] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(emptyBid);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/bids").then((r) => setBids(r.data.bids)).finally(() => setLoading(false));
  useEffect(() => { if (locked) return; load(); api.get("/reminders/upcoming").then((r) => setUpcoming(r.data.upcoming)).catch(() => {}); /* eslint-disable-next-line */ }, [locked]);
  useEffect(() => { setRemindersOn(user?.reminders_enabled !== false); }, [user]);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const s = params.get("crm_add");
    if (s === "ok") toast.success("Bid added to your CRM 🎯");
    else if (s === "invalid") toast.error("That link expired — open the bid from your dashboard instead.");
    if (s) {
      params.delete("crm_add");
      window.history.replaceState({}, "", window.location.pathname + (params.toString() ? `?${params}` : ""));
    }
  }, []);

  if (locked) return <UpgradeLock required="pro" feature="Bid CRM" description="Upgrade to Pro to track every pursuit through your pipeline and get deadline reminders." />;

  const saveBid = async () => {
    if (!form.title.trim()) { toast.error("Add a title for the bid"); return; }
    setSaving(true);
    try {
      const r = await api.post("/bids", { ...form, amount: Number(form.amount) || 0 });
      setBids((prev) => [r.data, ...prev]);
      setAddOpen(false); setForm(emptyBid);
      toast.success("Bid added to pipeline");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add bid");
    } finally { setSaving(false); }
  };

  const toggleReminders = async () => {
    const next = !remindersOn;
    setRemindersOn(next);
    try { await api.put("/reminders/settings", { reminders_enabled: next }); await refresh(); toast.success(next ? "Email reminders on" : "Email reminders off"); }
    catch (e) { setRemindersOn(!next); toast.error("Could not update"); }
  };

  const sendTest = async () => {
    setTesting(true);
    try { const r = await api.post("/reminders/test"); toast.success(`Test email sent to ${r.data.to}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send test email"); }
    finally { setTesting(false); }
  };

  const move = async (bid, stage) => {
    if (bid.stage === stage) return;
    setBids((prev) => prev.map((b) => (b.id === bid.id ? { ...b, stage } : b)));
    try { await api.patch(`/bids/${bid.id}`, { stage }); } catch (e) { toast.error("Update failed"); load(); }
  };

  const remove = async (id) => {
    setBids((prev) => prev.filter((b) => b.id !== id));
    try { await api.delete(`/bids/${id}`); toast.success("Removed"); } catch (e) { load(); }
  };

  if (loading) return <div className="h-64 flex items-center justify-center"><div className="h-8 w-8 border-[3px] border-slate-200 border-t-slate-900 rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="crm-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Bid CRM</h1>
          <p className="text-slate-500 mt-1">Drag bids across your pursuit pipeline. Add bids from Opportunities, Score Any Bid, or manually.</p>
        </div>
        <button onClick={() => { setForm(emptyBid); setAddOpen(true); }} data-testid="open-add-bid-button" className="shrink-0 bg-emerald-900 text-white rounded-md px-4 py-2.5 text-sm font-medium hover:bg-emerald-800 transition-colors inline-flex items-center gap-2">
          <Plus className="h-4 w-4" /> Add Bid
        </button>
      </div>

      {/* Deadline reminders */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4" data-testid="reminders-bar">
        <div className="flex items-start gap-3 min-w-0">
          <div className="h-10 w-10 rounded-lg bg-emerald-900 text-white flex items-center justify-center shrink-0">
            <CalendarClock className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="font-heading font-semibold text-slate-900">Deadline reminders</p>
            {upcoming.length > 0 ? (
              <p className="text-sm text-slate-500 truncate">
                Next: <span className="font-medium text-slate-700">{upcoming[0].title}</span> · due in <span className="text-red-600 font-semibold">{upcoming[0].days} day{upcoming[0].days !== 1 ? "s" : ""}</span>
                {upcoming.length > 1 && <span className="text-slate-400"> · +{upcoming.length - 1} more in next 14 days</span>}
              </p>
            ) : (
              <p className="text-sm text-slate-500">No bids due in the next 14 days. We'll email you 7, 3 &amp; 1 day before each deadline.</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={sendTest} disabled={testing} data-testid="send-test-email-button" className="bg-white border border-slate-200 text-slate-700 rounded-md px-3 py-2 text-sm font-medium hover:bg-slate-50 transition-colors inline-flex items-center gap-2 disabled:opacity-60">
            <Send className="h-4 w-4" /> {testing ? "Sending…" : "Send test"}
          </button>
          <button onClick={toggleReminders} data-testid="toggle-reminders-button" className={`rounded-md px-3 py-2 text-sm font-medium transition-colors inline-flex items-center gap-2 ${remindersOn ? "bg-emerald-900 text-white hover:bg-emerald-800" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
            {remindersOn ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />} {remindersOn ? "Reminders on" : "Reminders off"}
          </button>
        </div>
      </div>

      {bids.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-12 text-center" data-testid="crm-empty">
          <p className="text-slate-400">No bids yet. Score an opportunity, or add one manually to start tracking.</p>
          <button onClick={() => { setForm(emptyBid); setAddOpen(true); }} data-testid="empty-add-bid-button" className="mt-4 bg-emerald-900 text-white rounded-md px-4 py-2.5 text-sm font-medium hover:bg-emerald-800 transition-colors inline-flex items-center gap-2">
            <Plus className="h-4 w-4" /> Add your first bid
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          {STAGES.map((stage) => {
            const items = bids.filter((b) => b.stage === stage.id);
            return (
              <div key={stage.id} onDragOver={(e) => e.preventDefault()} onDrop={() => { const b = bids.find((x) => x.id === dragId); if (b) move(b, stage.id); }}
                className="bg-slate-100/60 rounded-xl p-3 min-h-[200px]" data-testid={`crm-column-${stage.id}`}>
                <div className="flex items-center justify-between mb-3 px-1">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{stage.label}</span>
                  <span className="font-data text-xs text-slate-400">{items.length}</span>
                </div>
                <div className="space-y-2">
                  {items.map((b) => (
                    <div key={b.id} draggable onDragStart={() => setDragId(b.id)}
                      className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm cursor-grab active:cursor-grabbing group" data-testid={`bid-card-${b.id}`}>
                      <div className="flex items-start justify-between gap-1">
                        <p className="text-sm font-medium text-slate-900 line-clamp-2">{b.title}</p>
                        <button onClick={() => remove(b.id)} data-testid={`delete-bid-${b.id}`} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500 transition-all shrink-0">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <p className="text-xs text-slate-400 mt-1 truncate">{b.agency}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="font-data text-xs text-slate-600">{fmt(b.amount)}</span>
                        {b.win_probability != null && <span className="text-xs font-semibold text-accent">{b.win_probability}% win</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-bid-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Add a bid</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Title *</label>
              <input autoFocus value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="add-bid-title" placeholder="e.g. City of Austin — Roadway Resurfacing" className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Issuing Organization</label>
                <input value={form.agency} onChange={(e) => setForm({ ...form, agency: e.target.value })} data-testid="add-bid-agency" placeholder="e.g. TxDOT" className={inputCls} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Amount ($)</label>
                <input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="add-bid-amount" placeholder="2500000" className={inputCls} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Deadline</label>
                <input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} data-testid="add-bid-deadline" className={inputCls} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Stage</label>
                <select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} data-testid="add-bid-stage" className={inputCls}>
                  {STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Notes</label>
              <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="add-bid-notes" placeholder="Link to the solicitation, scope notes, contacts…" className={inputCls} />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setAddOpen(false)} data-testid="add-bid-cancel" className="rounded-md px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors">Cancel</button>
            <button onClick={saveBid} disabled={saving} data-testid="add-bid-save" className="bg-emerald-900 text-white rounded-md px-4 py-2.5 text-sm font-medium hover:bg-emerald-800 transition-colors disabled:opacity-60">{saving ? "Adding…" : "Add Bid"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
